// api_channelModeration.ts — actions des super-admins sur la chaîne d'un créateur,
// depuis le Dashboard (2026-10-17) : l'éteindre, la verrouiller, la déverrouiller.
// admin-api ChannelModerationController ; `adminId` est vérifié côté serveur
// (ROLE_ADMIN). Une chaîne verrouillée ne peut plus être allumée par personne.
// Verrouiller exige un motif (10 caractères au moins), envoyé par e-mail au créateur ;
// chaque action est gardée dans l'historique de la chaîne.

import axios from 'axios';

import { API_URLS } from '../urls';

export interface ChannelLock {
  at        : string | null;
  byAdminId : number | null;
  by        : string;
  reason    : string;
}

export interface ChannelModerationEvent {
  at        : string;
  action    : 'stop' | 'lock' | 'unlock' | string;
  reason    : string | null;
  by        : string;
  emailTo   : string | null;
  emailSent : boolean;
}

export interface ChannelModerationState {
  id            : number;
  active        : boolean;
  channelStatus : string;
  lock          : ChannelLock | null;
  history       : ChannelModerationEvent[];
  /** Verrouillage seulement : l'e-mail envoyé au créateur. */
  email?        : { sent: boolean; to: string | null; error: string | null };
}

export const LOCK_REASON_MIN = 10;   // comme ChannelModerationController::REASON_MIN

export type ChannelModerationResult = { ok: true; state: ChannelModerationState } | { ok: false; error: string };

const post = async (template: string, channelId: number, body: Record<string, unknown>): Promise<ChannelModerationResult> => {
  try {
    const { data } = await axios.post(template.replace('{id}', String(channelId)), body, {
      headers: { 'Content-Type': 'application/json' },
      // L'extinction arrête conteneur, HAProxy et MediaMTX : une quinzaine de secondes.
      timeout: 90_000,
    });
    return { ok: true, state: data as ChannelModerationState };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return { ok: false, error: (error.response?.data as any)?.error ?? error.message };
    }
    return { ok: false, error: 'Erreur réseau' };
  }
};

export const stopChannel   = (channelId: number, adminId: number) => post(API_URLS.channelModerationStop, channelId, { adminId });
export const lockChannel   = (channelId: number, adminId: number, reason: string) => post(API_URLS.channelModerationLock, channelId, { adminId, reason });
export const unlockChannel = (channelId: number, adminId: number, note = '') => post(API_URLS.channelModerationUnlock, channelId, { adminId, reason: note });

/** Le verrou et l'historique d'une chaîne (lecture publique). */
export const getChannelModeration = async (channelId: number): Promise<ChannelModerationState | null> => {
  try {
    const { data } = await axios.get(API_URLS.channelModeration.replace('{id}', String(channelId)));
    return data as ChannelModerationState;
  } catch {
    return null;
  }
};
